import { CdkStepperModule } from '@angular/cdk/stepper';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatStepperModule } from '@angular/material/stepper';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { IdleExpiry, SimpleExpiry } from '@ng-idle/core';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { CookieModule, CookieService } from 'ngx-cookie';
import { AccountRegisterComponent } from './account/account-register/account-register.component';
import { AccountComponent } from './account/account.component';
import { LoginComponent } from './account/login/login.component';
import { AdministratorListComponent } from './account/userprofile/administratorlist/administrator-list.component';
import { UserinformationComponent } from './account/userprofile/userinformation/userinformation.component';
import { FilterPreferencesComponent } from './account/userprofile/userpreferences/filter-preferences/filter-preferences.component';
import { UserPreferencesComponent } from './account/userprofile/userpreferences/user-preferences.component';
import { UserProfileComponent } from './account/userprofile/userprofile.component';
import { AdministrationComponent } from './admin/administration.component';
import { ApprovalCommentComponent } from './admin/approvals/approval-comment/approval-comment.component';
import { ApprovalsComponent } from './admin/approvals/approvals.component';
import { BulkImportsComponent } from './admin/bulk-imports/bulk-imports.component';
import { ManageAgencyComponent } from './admin/manage-agency/manage-agency.component';
import { ManageAttachmentsComponent } from './admin/manage-attachments/manage-attachments.component';
import { ManageDuplicatesComponent } from './admin/manage-duplicates/manage-duplicates.component';
import { CompareAndMergePeopleComponent } from './admin/manage-duplicates/merge-people/compare-and-merge/compare-and-merge.component';
import { MergePeopleContainerComponent } from './admin/manage-duplicates/merge-people/merge-people-container.component';
import { MergePeopleComponent } from './admin/manage-duplicates/merge-people/merge-people.component';
import { ManageElasticSearchComponent } from './admin/manage-elasticsearch/manage-elasticsearch.component';
import { ManageFDGroupsComponent } from './admin/manage-fd-groups/manage-fd-groups.component';
import { ManageLeadershipComponent } from './admin/manage-leadership/manage-leadership.component';
import { ManageMyAgencyComponent } from './admin/manage-my-agency/manage-my-agency.component';
import { ManageSupervisorsComponent } from './admin/manage-supervisors/manage-supervisors.component';
import { CertifyUsersComponent } from './admin/manage-users/certify-users/certify-users.component';
import { EnableDisableComponent } from './admin/manage-users/enable-disable/enable-disable.component';
import { ManageUsersComponent } from './admin/manage-users/manage-users.component';
import { MergeAgenciesComponent } from './admin/merge-agencies/merge-agencies.component';
import { UserHistoryComponent } from './admin/user-list/user-history/user-history.component';
import { UserListComponent } from './admin/user-list/user-list.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ballisticEvidenceListComponent } from './ballistic-evidence/ballistic-evidence-list/ballistic-evidence-list.component';
import { ballisticEvidenceComponent } from './ballistic-evidence/ballistic-evidence.component';
import { CrimeGunDataComponent } from './crime-guns/crime-gun-detail/crime-gun-data/crime-gun-data.component';
import { CrimeGunDetailComponent } from './crime-guns/crime-gun-detail/crime-gun-detail.component';
import { CrimeGunFirearmDetailComponent } from './crime-guns/crime-gun-detail/crime-gun-firearm-detail/crime-gun-firearm-detail.component';
import { CrimeGunFirearmComponent } from './crime-guns/crime-gun-detail/crime-gun-firearm/crime-gun-firearm.component';
import { CrimeGunIrsComponent } from './crime-guns/crime-gun-detail/crime-gun-irs/crime-gun-irs.component';
import { CrimeGunMapComponent } from './crime-guns/crime-gun-detail/crime-gun-map/crime-gun-map.component';
import { CrimeGunResultsComponent } from './crime-guns/crime-gun-detail/crime-gun-results/crime-gun-results.component';
import { CrimeGunShootingsComponent } from './crime-guns/crime-gun-detail/crime-gun-shootings/crime-gun-shootings.component';
import { CrimeGunTriageComponent } from './crime-guns/crime-gun-detail/crime-gun-triage/crime-gun-triage.component';
import { TriageHistoryComponent } from './crime-guns/crime-gun-detail/crime-gun-triage/triage-history/triage-history.component';
import { ExportStepOutComponent } from './crime-guns/crime-gun-detail/export-step-out/export-step-out.component';
import { LinksHitsComponent } from './crime-guns/crime-gun-detail/links-hits/links-hits.component';
import { CrimeGunsComponent } from './crime-guns/crime-guns.component';
import { EventDetailComponent } from './events/event-detail/event-detail.component';
import { EventAttachmentsComponent } from './events/event-form-stepper/event-attachments.component';
import { EventDetailsComponent } from './events/event-form-stepper/event-details.component';
import { EventLocationsComponent } from './events/event-form-stepper/event-locations.component';
import { EventPeopleComponent } from './events/event-form-stepper/event-people.component';
import { EventStepperContainerComponent } from './events/event-form-stepper/event-stepper-container.component';
import { EventSummaryComponent } from './events/event-form-stepper/event-summary.component';
import { EventImportComponent } from './events/event-import/event-import.component';
import { EventsComponent } from './events/events.component';
import { ExportsGridComponent } from './exports/exports-grid/exports-grid.component';
import { ExportsComponent } from './exports/exports.component';
import { httpInterceptorProviders } from './http-interceptors/index';
import { RequestCache, RequestCacheWithMap } from './http-interceptors/request-cache.service';
import { InfoReportDetailComponent } from './info-reports/info-report-detail/info-report-detail.component';
import { EventsLeadsTargetsComponent } from './info-reports/info-report-form/events-leads-targets/events-leads-targets.component';
import { InfoReportFormComponent } from './info-reports/info-report-form/info-report-form.component';
import { InfoReportNotificationComponent } from './info-reports/info-report-form/info-report-notification/info-report-notification.component';
import { InfoReportReviewComponent } from './info-reports/info-report-form/info-report-review/info-report-review.component';
import { SummaryNotesComponent } from './info-reports/info-report-form/summary-notes/summary-notes.component';
import { TrackingComponent } from './info-reports/info-report-form/tracking/tracking.component';
import { InfoReportsNotesComponent } from './info-reports/info-reports-notes/info-reports-notes.component';
import { InfoReportsComponent } from './info-reports/info-reports.component';
import { LocationDetailComponent } from './locations/location-detail/location-detail.component';
import { LocationFormComponent } from './locations/location-form/location-form.component';
import { LocationsComponent } from './locations/locations.component';
import { VerifyLocationComponent } from './locations/verify-location/verify-location.component';
import { FindPersonContainerComponent } from './people/find-person/find-person-container.component';
import { FindPersonResultsComponent } from './people/find-person/find-person-results.component';
import { FindPersonComponent } from './people/find-person/find-person.component';
import { PeopleComponent } from './people/people.component';
import { PersonDetailComponent } from './people/person-detail/person-detail.component';
import { PersonFormComponent } from './people/person-form/person-form.component';
import { PersonSearchResultsComponent } from './people/person-search/person-search-results.component';
import { PersonSearchComponent } from './people/person-search/person-search.component';
import { ResultsDetailComponent } from './results/results-detail/results-detail.component';
import { ResultsFilterComponent } from './results/results-filter/results-filter.component';
import { ResultDefendantFormComponent } from './results/results-form/result-defendant-form/result-defendant-form.component';
import { ResultNotificationComponent } from './results/results-form/result-notification/result-notification.component';
import { ResultsFormComponent } from './results/results-form/results-form.component';
import { ResultsComponent } from './results/results.component';
import { SearchResultsComponent } from './search/search-results/search-results.component';
import { SearchComponent } from './search/search.component';
import { LoginService } from './services/login.service';
import { AddEventInlineComponent } from './shared/components/add-event-inline/add-event-inline.component';
import { AgencyCdsAssignApproverFormComponent } from './shared/components/agency-cds-assign-approver-form/agency-cds-assign-approver-form.component';
import { AgencyCDSDialogComponent } from './shared/components/agency-cds-dialog/agency-cds-dialog.component';
import { AgencyMouCdsFormComponent } from './shared/components/agency-mou-cds-form/agency-mou-cds-form.component';
import { BannerComponent } from './shared/components/banner/banner.component';
import { NessStepHeaderComponent } from './shared/components/custom-stepper/step-header.component';
import { NessStepperComponent } from './shared/components/custom-stepper/stepper.component';
import { DateRangeFilterStatusComponent } from './shared/components/date-range-filter/date-range-filter-status.component';
import { DateRangeFilterComponent } from './shared/components/date-range-filter/date-range-filter.component';
import { DeleteHistoryFormComponent } from './shared/components/delete-history-form/delete-history-form.component';
import { IncludeCDSFilterComponent } from './shared/components/include-cds-agencies/include-cds-agencies-filter.component';
import { FindExistingLocationComponent } from './shared/components/find-existing-location/find-existing-location.component';
import { GeographicFilterRegionStatusComponent } from './shared/components/geographic-filter/geographic-filter-region/geographic-filter-region-status.component';
import { GeographicFilterRegionComponent } from './shared/components/geographic-filter/geographic-filter-region/geographic-filter-region.component';
import { GeographicFilterStatusComponent } from './shared/components/geographic-filter/geographic-filter-status.component';
import { GeographicFilterComponent } from './shared/components/geographic-filter/geographic-filter.component';
import { HelpComponent } from './shared/components/help/help.component';
import { HomeComponent } from './shared/components/home/home.component';
import { InlineEditTextComponent } from './shared/components/inline-edit-text/inline-edit-text.component';
import { LandingPageComponent } from './shared/components/landing-page/landing-page.component';
import { LeftNavigationComponent } from './shared/components/left-navigation/left-navigation.component';
import { LEPartnerFormComponent } from './shared/components/lepartner/lepartner-form/lepartner-form.component';
import { LEPartnerSearchUserComponent } from './shared/components/lepartner/lepartner-search-user-form/lepartner-search-user-form.component';
import { LEPartnerViewComponent } from './shared/components/lepartner/lepartner-view-form/lepartner-view-form.component';
import { LinkedAgencyFilterComponent } from './shared/components/linked-agency-filter/linked-agency-filter.component';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { TableLoaderComponent } from './shared/components/loader/table-loader.component';
import { LocalFilterComponent } from './shared/components/local-filter/local-filter.component';
import { ManageCasesAlertComponent } from './shared/components/manage-cases-alert/manage-cases-alert.component';
import { ManageDetailsAlertComponent } from './shared/components/manage-details-alert/manage-details-alert.component';
import { ManualFTSIDComponent } from './shared/components/manual-ftsid/manual-ftsid.component';
import { MessageComponent } from './shared/components/message/message.component';
import { NIBINCaseDataComponent } from './shared/components/nibin-case-data/nibin-case-data.component';
import { PagedLoadingStatusComponent } from './shared/components/paged-loading-status/paged-loading-status.component';
import { SlAgencyFilterComponent } from './shared/components/sl-agency-filter/sl-agency-filter.component';
import { AutoFocusDirective } from './shared/directives/autofocus.directive';
import { KeyboardAccessibleLinksDirective } from './shared/directives/keyboard-accessible-links.directive';
import { RemoveIfNoAccessDirective } from './shared/directives/remove-if-no-access.directive';
import { RemoveIfNoPermission } from './shared/directives/remove-if-nopermission.directive';
import { RemoveIfUnauthorizedIRDirective } from './shared/directives/remove-if-unauthorized-ir.directive';
import { RemoveIfUnauthorizedResultDirective } from './shared/directives/remove-if-unauthorized-result.directive';
import { RemoveIfUnauthorized } from './shared/directives/remove-if-unauthorized.directive';
import { RemoveIfUnauthorizedDevMode } from './shared/directives/remove-if-unauthorizedDevMode.directive';
import { TrimValueAccessorDirective } from './shared/directives/trim-value-accessor.directive';
import { BooleanTransformIconPipe } from './shared/pipes/boolean-transform-icon.pipe';
import { BooleanTransformPipe } from './shared/pipes/boolean-transform.pipe';
import { CGICCodeToFDPipe } from './shared/pipes/cgiccode-to-fd.pipe';
import { DisplayPrimaryPhonePipe } from './shared/pipes/display-primary-phone.pipe';
import { EmptyTextTransformPipe } from './shared/pipes/empty-text-transform.pipe';
import { ExportStatusTransformPipe } from './shared/pipes/export-status-transform.pipe';
import { FilterPrimaryCg } from './shared/pipes/filter-primary-cg.pipe';
import { HtmlTransformPipe } from './shared/pipes/html-transform.pipe';
import { JoinArrayPipe } from './shared/pipes/join-array.pipe';
import { LineBreakTransformPipe } from './shared/pipes/line-break-transform.pipe';
import { LocalToUtcTransformPipe } from './shared/pipes/local-to-utc-transform.pipe';
import { PhoneNumberFormatPipe } from './shared/pipes/phone-number-format.pipe';
import { PhoneNumberTypeTransformPipe } from './shared/pipes/phone-number-type-transform.pipe';
import { ShortenTextTransformPipe } from './shared/pipes/shorten-text-transform.pipe';
import { SsnFormatPipe } from './shared/pipes/ssn-format.pipe';
import { TrimTextTransformPipe } from './shared/pipes/trim-text-transform.pipe';
import { UtcToLocalPipe } from './shared/pipes/utc-to-local.pipe';
import { CDSApproverProfileDirective } from './shared/directives/cds-approver-profile.directive';
import { PrimeNGUIModule } from './shared/primengui.module'; // Import any new PrimeNG modules to stand-alone module file
import { InfoTooltipComponent } from './shared/components/info-tooltip/info-tooltip.component';
import { TimeZonePipe } from './shared/pipes/time-zone.pipe';
import { DojLoginFormComponent } from './account/login/doj-login-form/doj-login-form.component';

@NgModule({
  declarations: [
    AppComponent,
    AccountComponent,
    AccountRegisterComponent,
    AddEventInlineComponent,
    BooleanTransformPipe,
    TrimTextTransformPipe,
    CDSApproverProfileDirective,
    CrimeGunsComponent,
    CrimeGunDetailComponent,
    CrimeGunDataComponent,
    CrimeGunIrsComponent,
    CrimeGunMapComponent,
    InfoReportDetailComponent,
    InfoReportFormComponent,
    InfoReportNotificationComponent,
    InfoReportReviewComponent,
    EventDetailComponent,
    EventImportComponent,
    LEPartnerFormComponent,
    LEPartnerSearchUserComponent,
    LEPartnerViewComponent,
    EventsComponent,
    EventsLeadsTargetsComponent,
    ExportStepOutComponent,
    HomeComponent,
    HelpComponent,
    InfoReportsComponent,
    LeftNavigationComponent,
    LineBreakTransformPipe,
    LoaderComponent,
    LocalToUtcTransformPipe,
    LocationDetailComponent,
    LocationFormComponent,
    LoginComponent,
    MessageComponent,
    PagedLoadingStatusComponent,
    PeopleComponent,
    PersonDetailComponent,
    PersonFormComponent,
    PersonSearchComponent,
    PersonSearchResultsComponent,
    RemoveIfNoAccessDirective,
    RemoveIfNoPermission,
    RemoveIfUnauthorized,
    RemoveIfUnauthorizedDevMode,
    ResultsComponent,
    ResultsDetailComponent,
    ResultNotificationComponent,
    ResultsFormComponent,
    SummaryNotesComponent,
    TableLoaderComponent,
    TrackingComponent,
    FilterPreferencesComponent,
    UserProfileComponent,
    UtcToLocalPipe,
    ShortenTextTransformPipe,
    SearchComponent,
    SearchResultsComponent,
    ExportsComponent,
    ResultsFilterComponent,
    ResultDefendantFormComponent,
    AdministrationComponent,
    ApprovalsComponent,
    ManageUsersComponent,
    BulkImportsComponent,
    ManageAgencyComponent,
    ManageSupervisorsComponent,
    EnableDisableComponent,
    CertifyUsersComponent,
    ManageAttachmentsComponent,
    ApprovalCommentComponent,
    LocationsComponent,
    VerifyLocationComponent,
    CrimeGunFirearmComponent,
    LinksHitsComponent,
    CrimeGunTriageComponent,
    ExportsGridComponent,
    PhoneNumberFormatPipe,
    ExportStatusTransformPipe,
    KeyboardAccessibleLinksDirective,
    UserListComponent,
    DeleteHistoryFormComponent,
    ManageDuplicatesComponent,
    MergePeopleComponent,
    MergePeopleContainerComponent,
    FindPersonComponent,
    FindPersonContainerComponent,
    FindPersonResultsComponent,
    CompareAndMergePeopleComponent,
    GeographicFilterStatusComponent,
    GeographicFilterComponent,
    LocalFilterComponent,
    DeleteHistoryFormComponent,
    UserPreferencesComponent,
    DateRangeFilterComponent,
    DateRangeFilterStatusComponent,
    IncludeCDSFilterComponent,
    InfoTooltipComponent,
    AdministratorListComponent,
    JoinArrayPipe,
    UserinformationComponent,
    UserHistoryComponent,
    ManualFTSIDComponent,
    BooleanTransformIconPipe,
    InfoReportsNotesComponent,
    CrimeGunResultsComponent,
    RemoveIfUnauthorizedResultDirective,
    SsnFormatPipe,
    ManageAgencyComponent,
    TrimValueAccessorDirective,
    CGICCodeToFDPipe,
    EventStepperContainerComponent,
    EventDetailsComponent,
    EventLocationsComponent,
    EventPeopleComponent,
    EventAttachmentsComponent,
    EventSummaryComponent,
    PhoneNumberTypeTransformPipe,
    DisplayPrimaryPhonePipe,
    FindExistingLocationComponent,
    AutoFocusDirective,
    ManualFTSIDComponent,
    MergeAgenciesComponent,
    FilterPrimaryCg,
    ManageLeadershipComponent,
    LandingPageComponent,
    ManageFDGroupsComponent,
    ManageElasticSearchComponent,
    NIBINCaseDataComponent,
    EmptyTextTransformPipe,
    RemoveIfUnauthorizedIRDirective,
    InlineEditTextComponent,
    CrimeGunFirearmDetailComponent,
    ballisticEvidenceComponent,
    ballisticEvidenceListComponent,
    CrimeGunShootingsComponent,
    GeographicFilterRegionComponent,
    GeographicFilterRegionStatusComponent,
    HtmlTransformPipe,
    SlAgencyFilterComponent,
    ManageCasesAlertComponent,
    LinkedAgencyFilterComponent,
    ManageDetailsAlertComponent,
    TriageHistoryComponent,
    DojLoginFormComponent,
    AgencyMouCdsFormComponent,
    AgencyCDSDialogComponent,
    AgencyCdsAssignApproverFormComponent,
    ManageMyAgencyComponent,
    NessStepperComponent,
    NessStepHeaderComponent,
    BannerComponent,
    TimeZonePipe
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    CookieModule.withOptions(),
    CommonModule,
    FormsModule,
    HttpClientModule,
    HttpClientXsrfModule.withOptions({
      cookieName: 'My-Xsrf-Cookie',
      headerName: 'My-Xsrf-Header',
    }),
    NgIdleKeepaliveModule.forRoot(),
    MatIconModule,
    CdkStepperModule,
    MatStepperModule,
    ReactiveFormsModule,
    PrimeNGUIModule
  ],
  providers: [
    { provide: RequestCache, useClass: RequestCacheWithMap },
    httpInterceptorProviders,
    { provide: IdleExpiry, useClass: SimpleExpiry },
    CookieService,
    LoginService,
    BooleanTransformPipe,
    MatIconRegistry
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
